/**
 * Version Service - Handles application version checking and cache management
 */

declare const __BUILD_TIME__: string;
declare const __APP_VERSION__: string;

interface VersionInfo {
  version: string;
  buildTime: string;
  lastChecked: string;
  lastUpdateShown?: string; // Track when we last showed an update modal
}

class VersionService {
  private readonly STORAGE_KEY = 'smartcounsellor_version_info';
  private readonly CHECK_INTERVAL = 30 * 60 * 1000; // 30 minutes (not used for automatic checking anymore)
  private checkIntervalId: ReturnType<typeof setInterval> | null = null; 

  /**
   * Get current application version
   */
  getCurrentVersion(): VersionInfo {
    return {
      version: __APP_VERSION__ || '1.0.0',
      buildTime: __BUILD_TIME__ || new Date().toISOString(),
      lastChecked: new Date().toISOString(),
    };
  }

  /**
   * Get stored version info from localStorage
   */
  private getStoredVersion(): VersionInfo | null {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.warn('Failed to parse stored version info:', error);
      return null;
    }
  }

  /**
   * Store version info in localStorage
   */
  private storeVersion(versionInfo: VersionInfo): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(versionInfo));
    } catch (error) {
      console.warn('Failed to store version info:', error);
    }
  }

  /**
   * Check if a new version is available
   */
  async checkForNewVersion(): Promise<boolean> {
    try {
      const currentVersion = this.getCurrentVersion();
      const storedVersion = this.getStoredVersion();

      // If no stored version, store current and return false
      if (!storedVersion) {
        this.storeVersion(currentVersion);
        return false;
      }

      // Check if we recently showed an update modal (within the last hour)
      if (storedVersion.lastUpdateShown) {
        const lastShown = new Date(storedVersion.lastUpdateShown).getTime();
        const now = new Date().getTime();
        const timeSinceLastShown = now - lastShown;
        const oneHour = 60 * 60 * 1000; // 1 hour
        
        if (timeSinceLastShown < oneHour) {
          console.log('Update modal shown recently, skipping check');
          return false;
        }
      }

      // Compare version strings first (more reliable than build time)
      if (currentVersion.version !== storedVersion.version) {
        
        // Mark that we're about to show an update
        const updatedVersion = {
          ...currentVersion,
          lastUpdateShown: new Date().toISOString()
        };
        this.storeVersion(updatedVersion);
        return true;
      }

      // Compare build times only if versions are the same
      const currentBuildTime = new Date(currentVersion.buildTime).getTime();
      const storedBuildTime = new Date(storedVersion.buildTime).getTime();

      // Only consider it a new version if the build time is significantly newer (more than 5 minutes)
      const timeDifference = currentBuildTime - storedBuildTime;
      const minimumUpdateThreshold = 5 * 60 * 1000; // 5 minutes

      if (timeDifference > minimumUpdateThreshold) {
        console.log('Build time change detected:', {
          current: currentVersion.buildTime,
          stored: storedVersion.buildTime,
          timeDifference
        });
        
        // Mark that we're about to show an update
        const updatedVersion = {
          ...currentVersion,
          lastUpdateShown: new Date().toISOString()
        };
        this.storeVersion(updatedVersion);
        return true;
      }

      return false;
    } catch (error) {
      console.warn('Failed to check for new version:', error);
      return false;
    }
  }

  /**
   * Start periodic version checking
   */
  startVersionChecking(onUpdateAvailable?: () => void): void {
    // Clear existing interval
    this.stopVersionChecking();

    // Start new interval
    this.checkIntervalId = setInterval(async () => {
      const hasUpdate = await this.checkForNewVersion();
      if (hasUpdate && onUpdateAvailable) {
        onUpdateAvailable();
      }
    }, this.CHECK_INTERVAL);

    // Do an initial check only if there's no stored version
    const storedVersion = this.getStoredVersion();
    if (!storedVersion) {
      this.checkForNewVersion().then((hasUpdate) => {
        if (hasUpdate && onUpdateAvailable) {
          onUpdateAvailable();
        }
      });
    }
  }

  /**
   * Stop periodic version checking
   */
  stopVersionChecking(): void {
    if (this.checkIntervalId) {
      clearInterval(this.checkIntervalId);
      this.checkIntervalId = null;
    }
  }

  /**
   * Force refresh the application
   */
  forceRefresh(): void {
    // Clear all caches
    this.clearApplicationCache();
    
    // Reload the page with cache bypass
    window.location.reload();
  }

  /**
   * Clear application cache
   */
  private clearApplicationCache(): void {
    try {
      // Clear localStorage version info
      localStorage.removeItem(this.STORAGE_KEY);

      // Clear all localStorage related to the app
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('smartcounsellor_')) {
          localStorage.removeItem(key);
        }
      });

      // Clear sessionStorage
      sessionStorage.clear();

      // If service worker is available, clear its cache
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations().then((registrations) => {
          registrations.forEach((registration) => {
            registration.unregister();
          });
        });
      }

      // Clear browser cache if available
      if ('caches' in window) {
        caches.keys().then((names) => {
          names.forEach((name) => {
            caches.delete(name);
          });
        });
      }
    } catch (error) {
      console.warn('Failed to clear application cache:', error);
    }
  }

  /**
   * Get application info for display
   */
  getAppInfo(): VersionInfo {
    return this.getCurrentVersion();
  }
}

// Export singleton instance
export const versionService = new VersionService();

// Export types
export type { VersionInfo };
